import { getSvgData } from '@/services/operations/svg';
import { SVG_QUERY_KEYS } from '@/services/query-keys/svg';
import type { GNBItemType } from '@/types';
import type { ApiResponse } from '@/types/api';
import type { QueryKey, UseQueryOptions } from '@tanstack/react-query';
import { useQueries } from '@tanstack/react-query';
import { useMemo } from 'react';

export const useGetSvgDataQuery = <TData = ApiResponse<string | React.ReactNode>>(
    gnbItems: GNBItemType[],
    options?: Omit<UseQueryOptions<ApiResponse<string | React.ReactNode>, unknown, TData, QueryKey>, 'queryKey' | 'queryFn'>
) => {
    const results = useQueries({
        queries: gnbItems?.map((item) => ({
            queryKey: typeof item.icon === 'string' ? SVG_QUERY_KEYS.getSvgData(item.icon) : ['svg-cached', item.id],
            queryFn: async (): Promise<ApiResponse<string | React.ReactNode>> => {
                if (typeof item.icon === 'string') {
                    const data = await getSvgData(item.icon);
                    return data;
                }
                return {
                    ok: true,
                    data: item.icon,
                };
            },
            staleTime: Infinity,
            gcTime: Infinity,
            ...options,
        })),
    });

    // Transform results to GNBItemType[] with populated icon data
    const gnbItemsWithSvg = useMemo(() => {
        return gnbItems.map((item, index) => {
            const query = results[index];

            // If query is loading or error, return original item
            if (!query.isSuccess || !query.data) {
                return item;
            }

            // If query is successful, populate icon with fetched data
            const apiResponse = query.data as ApiResponse<string | React.ReactNode>;
            const svgData = apiResponse.ok ? apiResponse.data : item.icon;

            return {
                ...item,
                icon: svgData,
            };
        });
    }, [gnbItems, results]);

    // Return both the transformed data and query states
    return {
        data: gnbItemsWithSvg,
        isLoading: results.some((query) => query.isLoading),
        isFetching: results.some((query) => query.isFetching),
        isSuccess: results.every((query) => query.isSuccess),
        isError: results.some((query) => query.isError),
        queries: results, // Original query results for fine-grained control
    };
};
